/**
 * Filter bar: date range, project, provider, user, client.
 *
 * Date range is the only filter sent to the API (`start`/`end` on
 * `/ai/usage/summary` and `/ai/usage/timeseries`). Project, provider, and
 * user narrow the already-fetched rows client-side in `AiUsageTab` --
 * the API has no `provider`/`user_id`/`client` query params (only `project_id`,
 * and even that isn't exposed here since there's no project picker
 * endpoint in scope; free-text substring match keeps this self-contained).
 */

import { type ReactElement } from 'react';

export const RANGE_PRESETS = [
  { days: 7, label: 'Last 7 days' },
  { days: 14, label: 'Last 14 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
] as const;

interface UsageFiltersProps {
  presetDays: number;
  onPresetDaysChange: (days: number) => void;
  project: string;
  onProjectChange: (value: string) => void;
  provider: string;
  onProviderChange: (value: string) => void;
  user: string;
  onUserChange: (value: string) => void;
  client: string;
  onClientChange: (value: string) => void;
}

export function UsageFilters({
  presetDays,
  onPresetDaysChange,
  project,
  onProjectChange,
  provider,
  onProviderChange,
  user,
  onUserChange,
  client,
  onClientChange,
}: UsageFiltersProps): ReactElement {
  return (
    <div className="usage-filters">
      <div className="usage-field">
        <label htmlFor="usage-filter-range">Range</label>
        <select
          id="usage-filter-range"
          value={presetDays}
          onChange={(e) => { onPresetDaysChange(Number(e.target.value)); }}
        >
          {RANGE_PRESETS.map((preset) => (
            <option key={preset.days} value={preset.days}>
              {preset.label}
            </option>
          ))}
        </select>
      </div>
      <div className="usage-field">
        <label htmlFor="usage-filter-project">Project</label>
        <input
          id="usage-filter-project"
          type="search"
          placeholder="All projects"
          value={project}
          onChange={(e) => { onProjectChange(e.target.value); }}
        />
      </div>
      <div className="usage-field">
        <label htmlFor="usage-filter-provider">Provider</label>
        <input
          id="usage-filter-provider"
          type="search"
          placeholder="All providers"
          value={provider}
          onChange={(e) => { onProviderChange(e.target.value); }}
        />
      </div>
      <div className="usage-field">
        <label htmlFor="usage-filter-user">User</label>
        <input
          id="usage-filter-user"
          type="search"
          placeholder="Filter by user…"
          value={user}
          onChange={(e) => { onUserChange(e.target.value); }}
        />
      </div>
      <div className="usage-field">
        <label htmlFor="usage-filter-client">Client</label>
        <input
          id="usage-filter-client"
          type="search"
          placeholder="All clients"
          value={client}
          onChange={(e) => { onClientChange(e.target.value); }}
        />
      </div>
    </div>
  );
}
